import React, { useState } from 'react';
import { ArrowRight, X, Settings, ChevronDown, ChevronUp, GripVertical, Plus } from 'lucide-react';

const typeColors = {
  trigger: 'bg-purple-100 border-purple-400 text-purple-800',
  action: 'bg-blue-100 border-blue-400 text-blue-800',
  condition: 'bg-yellow-100 border-yellow-400 text-yellow-800',
  output: 'bg-green-100 border-green-400 text-green-800',
};

const WorkflowNode = ({ node, index, onRemove, onConfigure, isLast }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="flex items-start gap-2">
      <div className={`relative flex flex-col items-center border-2 rounded-xl p-3 min-w-[140px] shadow-sm ${typeColors?.[node?.type] || typeColors?.action}`}>
        <div className="flex items-center gap-2 w-full">
          <GripVertical className="w-3.5 h-3.5 opacity-40 cursor-grab" />
          <node.icon className="w-4 h-4 flex-shrink-0" />
          <span className="text-xs font-semibold flex-1 truncate">{node?.label}</span>
          <div className="flex gap-1">
            <button onClick={() => setExpanded(!expanded)} className="opacity-60 hover:opacity-100 transition-opacity">
              {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
            <button onClick={() => onConfigure?.(node)} className="opacity-60 hover:opacity-100 transition-opacity">
              <Settings className="w-3 h-3" />
            </button>
            <button onClick={() => onRemove?.(index)} className="opacity-60 hover:opacity-100 transition-opacity">
              <X className="w-3 h-3" />
            </button>
          </div>
        </div>
        {expanded && (
          <div className="mt-2 w-full pt-2 border-t border-current border-opacity-20">
            <p className="text-xs opacity-70">Type: {node?.type}</p>
            {node?.config && Object.entries(node?.config)?.map(([k, v]) => (
              <p key={k} className="text-xs opacity-70">{k}: {v}</p>
            ))}
          </div>
        )}
        <span className={`absolute -top-2 -left-2 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold text-white ${
          node?.type === 'trigger' ? 'bg-purple-500' :
          node?.type === 'condition' ? 'bg-yellow-500' :
          node?.type === 'output' ? 'bg-green-500' : 'bg-blue-500'
        }`}>{index + 1}</span>
      </div>
      {!isLast && (
        <div className="flex items-center self-center">
          <ArrowRight className="w-5 h-5 text-gray-400" />
        </div>
      )}
    </div>
  );
};

const WorkflowCanvasEditor = ({ nodes, onRemoveNode, onConfigureNode, onAddBetween }) => {
  if (!nodes?.length) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl m-4">
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <Plus className="w-8 h-8 text-gray-400" />
          </div>
          <p className="text-sm font-medium text-gray-600">Start building your workflow</p>
          <p className="text-xs text-gray-400 mt-1">Click blocks from the left panel to add them here</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto p-6 bg-gray-50">
      <div
        className="min-h-full"
        style={{
          backgroundImage: 'radial-gradient(circle, #d1d5db 1px, transparent 1px)',
          backgroundSize: '24px 24px'
        }}
      >
        <div className="flex flex-wrap items-start gap-3 p-4">
          {nodes?.map((node, idx) => (
            <WorkflowNode
              key={node?.instanceId}
              node={node}
              index={idx}
              onRemove={onRemoveNode}
              onConfigure={onConfigureNode}
              isLast={idx === nodes?.length - 1}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default WorkflowCanvasEditor;
